import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Container, Button, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import debounce from "lodash.debounce";
import moment from "moment";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";


// Assuming these are in the correct relative paths
import "../../Pages/AddFund/AddFund.css";
import "./../Rewards/With.css";
import { fetchAllUsers } from "../../Actions/adminActions";
import apiCall from "./../../API/API";
import PaginationComponent from "../../Component/Pagination/PaginationControls";

// This is the combined component that includes both the registration form and the user list table.
const RegisterUser = () => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.admin);

  // --- STATE FOR REGISTRATION FORM ---
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [sponsorName, setSponsorName] = useState("");

  // --- STATE FOR USER LIST TABLE & FILTERS ---
  const [users, setUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("none");
  const [search, setSearch] = useState({
    name: "",
    username: "",
    uid: "",
    startDateJoining: "",
    endDateJoining: "",
    status: '',
    identify_user: 'fake'
  });
  const [limitFilter, setLimitFilter] = useState(20);
  const [dateFilter, setDateFilter] = useState("default");

  // --- STATE FOR MODALS & ACTIONS ---
  const [sponsorShow, setSponsorShow] = useState(false);
  const [uid, setUid] = useState();
  const [userName, setUserName] = useState();
  const [sponsorUsername, setSponsorUsername] = useState();
  const [newSponsor, setNewSponsor] = useState();
  const [existUserName, setExistUserName] = useState();
  const [openDialogue, setOpenDialogue] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  // const [currentPassword, setCurrentPassword] = useState(""); // ❌ REMOVED: No longer needed
  const [newPassword, setNewPassword] = useState("");


  const token = localStorage.getItem("authToken");

  // --- FUNCTIONS FOR REGISTRATION FORM ---

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    if (sponsor) {
      setTypingTimeout(
        setTimeout(() => {
          checkSponsorExistence();
        }, 500)
      );
    } else {
      setSponsorName("");
    }
    return () => clearTimeout(typingTimeout);
  }, [sponsor]);

  const checkSponsorExistence = async () => {
    setLoading(true);
    try {
      const { data, error } = await apiCall("post", "/check_username", {
        username: sponsor,
      });
      if (data) {
        setSponsorName(data.name);
      } else if (error) {
        setSponsorName("Sponsor not found");
      }
    } catch (err) {
      toast.error("An error occurred while checking sponsor.");
      console.error("Sponsor Check Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setEmail("");
    setMobile("");
    setPassword("");
    setSponsor("");
    setWalletAddress("");
    setSponsorName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const requestBody = {
      name,
      email,
      mobile,
      password,
      sponsor,
      wallet_address: walletAddress,
    };

    try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
          },
        };
      const { data, error } = await apiCall(
        "post",
        "/user-Register-fromAdmin",
        requestBody,
        config
      );

      if (data) {
        toast.success(data.message || "User registered successfully!");
        handleCancel();
        dispatch(fetchAllUsers({ page: 1, limit: limitFilter }));
      } else if (error) {
        toast.error(error.message || "Failed to register user.");
      }
    } catch (err) {
      toast.error("An error occurred during registration.");
      console.error("Registration Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- FUNCTIONS FOR USER LIST TABLE (from AllUsers) ---

  const fetchUsers = useCallback(
    debounce((params) => {
      setTableLoading(true);
      dispatch(fetchAllUsers(params)).finally(() => setTableLoading(false));
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      ...Object.fromEntries(
        Object.entries(search)?.filter(
          ([_, value]) => value !== "" && value !== 0
        )
      ),
    };
    fetchUsers(queryParams);
  }, [currentPage, limitFilter, search, fetchUsers]);

  useEffect(() => {
    if (userData) {
      setUsers(userData.data || []);
      setTotalPages(userData.totalPages || 1);
    }
  }, [userData]);


  const toggleFilter = () => setFilter(filter === "none" ? "flex" : "none");

  const handlePagination = (page) => setCurrentPage(page);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (e) => {
    setLimitFilter(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    const today = moment();
    let startDateJoining = "", endDateJoining = "";
    switch (value) {
      case "today":
        startDateJoining = today.startOf("day").format("YYYY-MM-DD");
        endDateJoining = today.endOf("day").format("YYYY-MM-DD");
        break;
      case "yesterday":
        const yesterday = moment().subtract(1, 'days');
        startDateJoining = yesterday.startOf("day").format("YYYY-MM-DD");
        endDateJoining = yesterday.endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        startDateJoining = today.startOf("week").format("YYYY-MM-DD");
        endDateJoining = today.endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        startDateJoining = today.startOf("month").format("YYYY-MM-DD");
        endDateJoining = today.endOf("month").format("YYYY-MM-DD");
        break;
      default:
        break;
    }
    setSearch(prev => ({...prev, startDateJoining, endDateJoining}));
  };

  const AddFilter = () => {
     const queryParams = {
      page: 1,
      limit: limitFilter,
      ...Object.fromEntries(
        Object.entries(search).filter(
          ([_, value]) => value !== "" && value !== 0
        )
      ),
    };
    setCurrentPage(1);
    dispatch(fetchAllUsers(queryParams));
  }

  const resetFilter = () => {
    setSearch({
      name: "",
      username: "",
      uid: "",
      startDateJoining: "",
      endDateJoining: "",
      status: '',
      identify_user: 'fake'
    });
    setLimitFilter(20);
    setDateFilter("default");
    setCurrentPage(1);
    dispatch(fetchAllUsers({ page: 1, limit: 20 }));
  };

  // --- SPONSOR & UPDATE MODAL FUNCTIONS ---

  const handleSponsorShow = (uid, sponsorUsername, username) => {
    setUid(uid);
    setSponsorUsername(sponsorUsername);
    setUserName(username);
    setSponsorShow(true);
  };

  const handleSponsorChange = async () => {
    try {
      const body = { sponsor_username: newSponsor };
      const res = await apiCall("POST", `/change_sponsor?uid=${uid}`, body);
      console.log("fdsfsd", res);
      toast.success(res?.data?.message);
      setNewSponsor('');
      setSponsorShow(false);
      setOpenDialogue(true);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleDialogueClose = () => {
    setOpenDialogue(false);
    setSponsorShow(false);
    dispatch(fetchAllUsers({ page: currentPage, limit: limitFilter, ...search }));
  };

  const handleUpdateModalShow = (user) => {
    setSelectedUserForEdit({...user});
    setNewPassword("");
    setShowUpdateModal(true);
  };

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false);
    setSelectedUserForEdit(null);
  };

  const handleUpdateUserChange = (e) => {
    const { name, value } = e.target;
    setSelectedUserForEdit(prev => ({...prev, [name]: value}));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    // Create a mutable copy to avoid direct state mutation issues
    const submissionData = { ...selectedUserForEdit };

    // CHANGE: Reformat dates to DD-MM-YYYY before sending to the API
    if (submissionData.joining_date) {
        submissionData.joining_date = moment(submissionData.joining_date).format("DD-MM-YYYY");
    }
    if (submissionData.Activation_date) {
        submissionData.Activation_date = moment(submissionData.Activation_date).format("DD-MM-YYYY");
    }

    // ✅ CHANGED: Send `newPassword`'s value in the `currentPassword` field.
    const payload = {
        ...submissionData,
        currentPassword: newPassword,
    };

    try {
        const {data, error} = await apiCall("POST", "/update_fake_user_profile", payload);
        if(data){
            toast.success(data.message || "User updated successfully!");
            handleUpdateModalClose();
            dispatch(fetchAllUsers({ page: currentPage, limit: limitFilter, ...search }));
        } else if (error) {
            toast.error(error.message || "Failed to update user.");
        }
    } catch (err) {
        toast.error("An error occurred during the update.");
    }
  };


  return (
    <React.Fragment>
      <Container className="mt-4">
        {/* --- SPONSOR CHANGE POPUP --- */}
        {sponsorShow && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Change Sponsor</h2>
              <div className="sponsorDiv">
                <div className="sponsorData">
                  <label>Username:</label>
                  <input name="username" value={userName} readOnly />
                </div>
                <div className="sponsorData">
                  <label>Sponsor Username:</label>
                  <input name="sponsorUsername" value={sponsorUsername} readOnly />
                </div>
                <div className="sponsorData">
                  <div className="d-flex justify-content-between w-100">
                    <label>New Sponsor Username:</label>
                    <label style={{ textAlign: "end" }} htmlFor="newSponsor">
                      {loading && newSponsor ? "Loading..." : existUserName}
                    </label>
                  </div>
                  <input name="newSponsor" placeholder="Sponsor Username" value={newSponsor} onChange={(e) => setNewSponsor(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={handleSponsorChange}>Submit</Button>
                  <Button variant="secondary" onClick={() => setSponsorShow(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- CONFIRMATION DIALOGUE --- */}
        <Dialog open={openDialogue} onClose={handleDialogueClose}>
          <DialogTitle>Confirmation Sponsor Change</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have changed Sponsor Username for <strong>{userName}</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogueClose}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* --- UPDATE USER MODAL --- */}
        <Modal show={showUpdateModal} onHide={handleUpdateModalClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Update User Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {selectedUserForEdit && (
                    <form onSubmit={handleUpdateSubmit}>
                         <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateUid">UID</label>
                                    <input type="text" id="updateUid" name="uid" className="form-control" value={selectedUserForEdit.uid || ''} onChange={handleUpdateUserChange} readOnly />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateName">Full Name</label>
                                    <input type="text" id="updateName" name="name" className="form-control" value={selectedUserForEdit.name || ''} onChange={handleUpdateUserChange} />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                             <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateUsername">Username</label>
                                    <input type="text" id="updateUsername" name="username" className="form-control" value={selectedUserForEdit.username || ''} onChange={handleUpdateUserChange} />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateEmail">Email</label>
                                    <input type="email" id="updateEmail" name="email" className="form-control" value={selectedUserForEdit.email || ''} onChange={handleUpdateUserChange} />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                           <Col md={6}>
                                 <div className="mb-3">
                                    <label htmlFor="updateMobile">Contact Number</label>
                                    <PhoneInput id="updateMobile" name="mobile" international defaultCountry="IN" value={selectedUserForEdit.mobile} onChange={(value) => setSelectedUserForEdit(prev => ({...prev, mobile: value}))} />
                                </div>
                            </Col>
                             <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="newPassword">Set New Password</label>
                                    <input type="password" id="newPassword" name="newPassword" className="form-control" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter New Password" />
                                </div>
                            </Col>
                        </Row>
                         <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateJoiningDate">Joining Date</label>
                                    <input type="datetime-local" id="updateJoiningDate" name="joining_date" className="form-control" value={selectedUserForEdit.joining_date ? moment(selectedUserForEdit.joining_date).format("YYYY-MM-DDTHH:mm") : ''} onChange={handleUpdateUserChange} />
                                </div>
                            </Col>
                             <Col md={6}>
                                <div className="mb-3">
                                    <label htmlFor="updateActivationDate">Activation Date</label>
                                    <input type="datetime-local" id="updateActivationDate" name="Activation_date" className="form-control" value={selectedUserForEdit.Activation_date ? moment(selectedUserForEdit.Activation_date).format("YYYY-MM-DDTHH:mm") : ''} onChange={handleUpdateUserChange} />
                                </div>
                            </Col>
                        </Row>
                        <Button variant="secondary" onClick={handleUpdateModalClose} className="me-2">Cancel</Button>
                        <Button variant="primary" type="submit">Update User</Button>
                    </form>
                )}
            </Modal.Body>
        </Modal>


        {/* --- SECTION 1: REGISTRATION FORM --- */}
        <section id="register-user-section">
          <div className="d-flex justify-content-center">
            <h2>Register Dummy User</h2>
          </div>
          <section id="formContent">
            <div className="form">
              <Row>
                <Col lg={6} style={{ margin: "auto" }}>
                  <form onSubmit={handleSubmit}>
                    <div id="formFields">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="sponsor">Sponsor Username:</label>
                          <label style={{ textAlign: "end" }}>
                            {loading && sponsor ? "Checking..." : sponsorName}
                          </label>
                        </div>
                        <input
                          type="text"
                          id="sponsor"
                          className="form-control"
                          value={sponsor}
                          onChange={(e) => setSponsor(e.target.value)}
                          placeholder="Enter sponsor's username"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="name">User Full Name:</label>
                        <input
                          type="text"
                          id="name"
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter User's full name"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="email">Email:</label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter User's email"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="phoneInput">Contact Number:</label>
                        <PhoneInput
                          id="phoneInput"
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="IN"
                          value={mobile}
                          onChange={setMobile}
                          placeholder="Enter user's mobile number"
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="walletAddress">Wallet Address:</label>
                        <input
                          type="text"
                          id="walletAddress"
                          className="form-control"
                          value={walletAddress}
                          onChange={(e) => setWalletAddress(e.target.value)}
                          placeholder="Enter user's wallet address"
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password">Password:</label>
                        <input
                          type="password"
                          id="password"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Set user's password"
                          required
                        />
                      </div>
                      <div className="d-flex justify-content-between">
                        <Button variant="secondary" className="me-2" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                          {loading ? "Registering..." : "Register User"}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Col>
              </Row>
            </div>
          </section>
        </section>

        {/* --- SECTION 2: USER LIST TABLE --- */}
        <section id="user-list-section" className="mt-5">
            <div className="d-flex justify-content-center mb-3">
                <h2>Registered Users List</h2>
            </div>
            <Row id="filterRow">
                <span id="filterLogo1" onClick={toggleFilter}>
                    <i><BiFilter /></i> Filter
                </span>
                <Row className="p-0 m-0" style={{ display: filter }}>
                    <Col lg="2" md="4"><input type="text" placeholder="Search User By Username" name="username" value={search.username} onChange={handleChange} /></Col>
                    <Col lg="2" md="4"><input type="text" placeholder="Search User By Uid" name="uid" value={search.uid} onChange={handleChange} /></Col>
                    <Col lg="2" md="4"><input type="date" placeholder="from date" name="startDateJoining" value={search.startDateJoining} onChange={handleChange} /></Col>
                    <Col lg="2" md="4"><input type="date" placeholder="to date" name="endDateJoining" value={search.endDateJoining} onChange={handleChange} /></Col>
                    <Col lg="2" md="4">
                        <select name="dateFilter" value={dateFilter} onChange={handleDateFilterChange}>
                            <option value="default">Default</option>
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="this_week">This Week</option>
                            <option value="this_month">This Month</option>
                        </select>
                    </Col>
                    <Col lg="2" md="4">
                        <select name="limit" value={limitFilter} onChange={handleLimitChange}>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                        </select>
                    </Col>
                     <Col lg="2" md="4">
                       <select name="status" value={search.status} onChange={handleChange}>
                           <option value="">--Status--</option>
                           <option value="1">Active</option>
                           <option value="0">Inactive</option>
                       </select>
                     </Col>
                    <Col lg="12">
                        <div>
                           <button className="save filter" onClick={AddFilter}>
                             Filter{" "}<i><BiFilter /></i>
                           </button>
                           <button className="cancel reset" onClick={resetFilter}>
                             Reset{" "}<i><GrPowerReset /></i>
                           </button>
                        </div>
                    </Col>
                </Row>
            </Row>
            <div id="directIncomeDiv">
                <Row id="directTableRow" className="text-center">
                    <Col md="12" className="p-0">
                        <div style={{ width: "100%", overflowX: "auto" }}>
                            <table>
                                    <tr>
                                        <th>S No.</th>
                                        <th>Name</th>
                                        <th>Username</th>
                                        <th>Uid</th>
                                        <th>Joining Date</th>
                                        <th>Activation Date</th>
                                        <th>Self Investment</th>
                                        <th>Main Wallet</th>
                                        <th>Fund Wallet</th>
                                        <th>Wallet Address</th>
                                        <th>Sponsor</th>
                                        <th>Sponsor Name</th>
                                        <th>Change Sponsor</th>
                                        <th>Action</th>
                                    </tr>
                                <tbody>
                                    {tableLoading ? (
                                        <tr><td colSpan="14">Loading...</td></tr>
                                    ) : users.length > 0 ? (
                                        users.map((user, i) => (
                                            <tr key={user._id}>
                                                <td>{i + 1 + limitFilter * (currentPage - 1)}</td>
                                                <td>{user?.name}</td>
                                                <td>
                                                  <div
                                                    className={`status-dot ${
                                                      user?.status === 0 ? "inactive" : "active"
                                                    }`}
                                                  />
                                                  {user?.username}
                                                </td>
                                                <td>{user?.uid}</td>
                                                <td>
                                                  {user?.joining_date ? moment(user.joining_date).format("MM-DD-YYYY HH:mm") : 'N/A'}
                                                </td>
                                                <td>
                                                  {user?.Activation_date ? moment(user.Activation_date).format("MM-DD-YYYY HH:mm") : "N/A"}
                                                </td>
                                                <td>{user?.self_investment || 0}</td>
                                                <td>{user?.main_wallet || 0}</td>
                                                <td>{user?.fund_wallet || 0}</td>
                                                <td>{user?.wallet_address || 'N/A'}</td>
                                                <td>{user?.sponsor_username || 'N/A'}</td>
                                                <td>{user?.sponsor_name || 'N/A'}</td>
                                                <td>
                                                    <button className="block-btn" onClick={() => handleSponsorShow(user?.uid, user?.sponsor_username, user?.username)}>
                                                        Change Sponsor
                                                    </button>
                                                </td>
                                                <td>
                                                    <Button variant="info" onClick={() => handleUpdateModalShow(user)}>Update</Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="14">No users found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>
            </div>
            <Row>
                <Col md="12">
                    {totalPages > 1 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePagination}
                        />
                    )}
                </Col>
            </Row>
        </section>
      </Container>
      {/* <ToastContainer className="toast-container" /> */}
    </React.Fragment>
  );
};

export default RegisterUser;